import {
  CompanyVariantFreeze,
  CompanyVariantFreezeWithCount,
  CompanyVariantFreezeWithVariants,
  CreateVariantFreezeRequest,
  UpdateVariantFreezeRequest,
} from '@upright/common/model/input/company-variants/api-types';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

export function useCompanyVariantFreezes() {
  return useQuery({
    queryKey: ['companyVariantFreezes'],
    queryFn: async () => await fetchOrThrow<CompanyVariantFreezeWithCount[]>('/company-variant-freezes'),
  });
}

interface FreezeQueryParams {
  limit: number;
  offset: number;
  releases?: string[];
  scenarioIds?: string[];
}

export function getCompanyVariantFreezeKey(freezeId: string, params?: FreezeQueryParams) {
  return ['companyVariantFreeze', freezeId, params];
}
export function useCompanyVariantFreeze(freezeId: string, params?: FreezeQueryParams) {
  const searchParams = new URLSearchParams();
  if (params) {
    searchParams.set('limit', String(params.limit));
    searchParams.set('offset', String(params.offset));
    for (const release of params.releases ?? []) {
      searchParams.append('release', release);
    }
    for (const scenarioId of params.scenarioIds ?? []) {
      searchParams.append('scenarioId', scenarioId);
    }
  }
  const queryString = searchParams.toString();
  const url = `/company-variant-freeze/${freezeId}${queryString ? `?${queryString}` : ''}`;
  return useQuery({
    queryKey: getCompanyVariantFreezeKey(freezeId, params),
    queryFn: async () => await fetchOrThrow<CompanyVariantFreezeWithVariants>(url),
  });
}

export function useCreateCompanyVariantFreezeMutation() {
  const navigate = useNavigate();
  const invalidateQueries = useInvalidateQueries();
  return useMutation({
    mutationKey: ['createCompanyVariantFreeze'],

    mutationFn: async (payload: CreateVariantFreezeRequest) =>
      await fetchOrThrow<CompanyVariantFreeze>('/company-variant-freeze', 'POST', payload),

    onSuccess: freeze => {
      invalidateQueries();
      navigate(`/internal/company-variant-freezes/${freeze.id}`);
    },
  });
}

export function useUpdateCompanyVariantFreezeMutation(freezeId: string) {
  const navigate = useNavigate();
  const invalidateQueries = useInvalidateQueries();
  return useMutation<CompanyVariantFreeze, Error, CreateVariantFreezeRequest>({
    mutationKey: ['updateCompanyVariantFreeze'],

    mutationFn: async (payload: UpdateVariantFreezeRequest) =>
      await fetchOrThrow<CompanyVariantFreeze>(`/company-variant-freeze/${freezeId}`, 'PATCH', payload),

    onSuccess: () => {
      invalidateQueries();
      navigate(`/internal/company-variant-freezes/${freezeId}`);
    },
  });
}

export function useDeleteCompanyVariantFreezeMutation(freezeId: string) {
  const navigate = useNavigate();
  const invalidateQueries = useInvalidateQueries();
  return useMutation({
    mutationKey: ['updateCompanyVariantFreeze'],
    mutationFn: async () => await fetchOrThrow(`/company-variant-freeze/${freezeId}`, 'DELETE'),

    onSuccess: () => {
      invalidateQueries();
      navigate(`/internal/company-variant-freezes`);
    },
  });
}

function useInvalidateQueries() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: ['companyVariantFreezes'],
    });
    void queryClient.invalidateQueries({
      queryKey: ['companyVariantFreeze'],
    });
  };
}
